import { useCallback, useEffect, useState } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../api/client.js";
import { useAuth } from "../../auth/AuthContext.js";
import { ArtifactIdentity, CopyIdButton } from "../../components/ArtifactIdentity.js";
import { useReviewEventStream } from "../../hooks/useReviewEventStream.js";
import { imageRequestDetailPath } from "./image/ImageRequestsList.js";
import { OPERATOR_ACTION_ROLE, effectiveImageRole, historyOutcomeLabel, roleAtLeast } from "./image/image-ui.js";
import { matchesHistoryFilter, mergeHistoryItems, type HistoryItem } from "./history-items.js";
import type { ImageBatchListItem, ImagePersonaOption, PersonaSourceStillSummary, VideoHistoryRow, VideoProgress } from "../../api/types.js";

interface JobDetail {
  readonly id: string;
  readonly jobType: string;
  readonly engine: string;
  readonly modelId: string | null;
  readonly status: string;
  readonly providerPredictionId: string | null;
  readonly estimatedCostUsd: number | null;
  readonly actualCostUsd: number | null;
  readonly visualModificationOutcome: string | null;
  readonly lastProviderError: string | null;
  readonly needsManualReconciliation: boolean;
  readonly failureReason?: string | null;
}

function StatusBadge({ status }: { readonly status: string }) {
  return <span className={`badge status-${status}`}>{status.replace(/_/g, " ")}</span>;
}

export type HistoryFilter = "ALL" | "ACTIVE" | "FINALIZED" | "FAILED" | "REJECTED" | "NEEDS_ATTENTION";
const FILTERS: readonly HistoryFilter[] = ["ALL", "ACTIVE", "FINALIZED", "FAILED", "REJECTED", "NEEDS_ATTENTION"];

/**
 * Final Review gate correction (2026-09): `ready_for_final_review` belongs
 * to REVIEWS exclusively — the real final asset exists but no human has
 * decided Final Approve/Reject on it yet, exactly like Gate 2's own
 * `ready_for_review` never appears in HISTORY either. Checked BEFORE
 * `matchesFilter` runs at all, so it is hidden under every filter tab
 * including "ALL" — never just excluded from one specific tab. Only once
 * a human decides (`finalized` or `final_review_rejected`) does the row
 * belong in HISTORY.
 */
export function isHistoryEligible(video: VideoHistoryRow): boolean {
  return video.status !== "ready_for_final_review";
}

export function matchesFilter(video: VideoHistoryRow, filter: HistoryFilter): boolean {
  switch (filter) {
    case "ALL":
      return true;
    case "ACTIVE":
      return video.outcomeLabel.startsWith("ACTIVE");
    case "FINALIZED":
      return video.status === "finalized";
    case "FAILED":
      return video.status === "failed";
    case "REJECTED":
      // Final Review gate correction (2026-09): a video rejected at the LAST gate (final_review_rejected) is just as much a "rejected" outcome as a Gate 2 rejection — both are the same decision level and both belong in this filter.
      return video.status === "rejected" || video.status === "final_review_rejected";
    case "NEEDS_ATTENTION":
      return video.failureDetail?.needsManualReconciliation === true;
  }
}

/** A rough, honestly-labeled estimate from the SAME real per-stage prices this pipeline already charges (video recreation ~$0.08, visual modification ~$0.40 when requested) — never a fabricated precise total. */
function estimateGate2Cost(hasVisualModification: boolean): string {
  return hasVisualModification ? "~$0.08 (video recreation) + ~$0.40 (visual modification)" : "~$0.08 (video recreation)";
}

/**
 * Real-time HISTORY (2026-09-08, user-requested): both sections below
 * used to fetch exactly once on mount, so a video reaching a new status
 * (most notably Gate 3 Final Approve/Reject, which flips it to
 * `finalized`/`final_review_rejected`) never appeared here until the
 * user navigated away and back or reloaded the page — the underlying DB
 * write itself was already instantaneous (`DecideFinalVideoReview` is a
 * synchronous `videoRepository.save()` in the same request as the
 * approval), the gap was purely "the UI never re-fetches on its own."
 * `useReviewEventStream` (shared with REVIEWS) subscribes to the SAME
 * SSE channel every Gate 1/Gate 2/Gate 3 write already publishes on —
 * `"save"` is one of `VIDEO_REPOSITORY_MUTATING_METHODS`, so
 * `DecideFinalVideoReview`'s own save already fires this push with zero
 * backend changes needed. The polling interval below is kept as the
 * same slower safety net REVIEWS already relies on, in case the SSE
 * connection is down.
 */
const HISTORY_REFRESH_INTERVAL_MS = 15_000;

function GateOneApprovalsSection({ tenantId, basePath }: { readonly tenantId: string; readonly basePath: string }) {
  const navigate = useNavigate();
  const [items, setItems] = useState<PersonaSourceStillSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<"continue" | "retry" | null>(null);

  const load = useCallback(async () => {
    try {
      // Unfiltered — HISTORY is the permanent record of every approval, active or not.
      const result = await api.get<{ artifacts: PersonaSourceStillSummary[] }>(`/videos/${tenantId}/persona-source-stills?status=persona_swap_approved`);
      setItems(result.artifacts);
      // A successful refresh clears a stale error from an earlier failed poll/action — no page refresh needed.
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof ApiError ? loadError.message : "Failed to load Gate 1 approvals.");
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Same real-time push as ListView below — this section's own
  // `gate2VideoStatus` column is exactly the kind of video-status change
  // the push exists for, so it must not go stale while the table beneath
  // it updates instantly.
  useReviewEventStream(tenantId, load);

  useEffect(() => {
    const interval = setInterval(() => void load(), HISTORY_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  async function runAction(item: PersonaSourceStillSummary, action: "continue" | "retry"): Promise<void> {
    setBusyId(item.id);
    setConfirmId(null);
    setConfirmAction(null);
    try {
      const path = action === "continue" ? "continue-gate2" : "retry-gate2";
      const result = await api.post<{ videoId?: string }>(`/videos/${tenantId}/persona-source-stills/${item.id}/${path}`, null, {});
      await load();
      if (action === "retry" && result.videoId) {
        navigate(`${basePath}/${result.videoId}`);
      }
    } catch (actionError) {
      setError(actionError instanceof ApiError ? actionError.message : "Action failed.");
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <div className="error-banner">{error}</div>;
  if (items.length === 0) return null;

  return (
    <div className="card">
      <h3>Gate 1 Approvals</h3>
      <table>
        <thead>
          <tr>
            <th>Artifact</th>
            <th>Approved</th>
            <th>Requested changes</th>
            <th>Gate 2 status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const hasVisualModification = item.progress.requestedChangesDescription !== null;
            return (
              <tr key={item.id}>
                <td>
                  <ArtifactIdentity
                    id={item.id}
                    thumbnailSrc={item.swappedFrameGcsUri !== null ? `/videos/${tenantId}/persona-source-stills/${item.id}/preview` : null}
                    label={new Date(item.createdAt).toLocaleDateString()}
                  />
                </td>
                {/* Two-fixes-follow-up (2026-09): use the real approval timestamp, not createdAt (when the artifact was made, not when it was approved) — approvedAt is already returned by the API, was just never read here. */}
                <td>{item.approvedAt !== null ? new Date(item.approvedAt).toLocaleString() : "—"}</td>
                <td>{item.progress.requestedChangesDescription ?? "—"}</td>
                <td>{item.gate2VideoStatus ?? (item.gate2AutoStartVideoId !== null ? "unknown" : item.gate2AutoStartRequestedAt !== null ? "starting…" : "not started")}</td>
                <td>
                  {/* DEFECT 2 fix: an artifact whose Gate 2 start has already been requested (by an immediate-start approval, or a prior Continue click) must never offer "Continue to Gate 2" again — that would misleadingly imply nothing is happening yet, when a start is already in flight or awaiting the background safety-net cycle. */}
                  {item.gate2AutoStartVideoId === null && item.gate2AutoStartRequestedAt !== null ? (
                    <span className="muted">Gate 2 starting…</span>
                  ) : item.gate2AutoStartVideoId === null ? (
                    confirmId === item.id && confirmAction === "continue" ? (
                      <span>
                        Start Gate 2? Estimated cost: {estimateGate2Cost(hasVisualModification)}{" "}
                        <button className="primary" disabled={busyId === item.id} onClick={() => void runAction(item, "continue")}>
                          Confirm
                        </button>
                        <button onClick={() => setConfirmId(null)}>Cancel</button>
                      </span>
                    ) : (
                      <button
                        className="primary"
                        disabled={busyId === item.id}
                        onClick={() => {
                          setConfirmId(item.id);
                          setConfirmAction("continue");
                        }}
                      >
                        Continue to Gate 2
                      </button>
                    )
                  ) : item.gate2VideoStatus === "failed" || item.gate2VideoStatus === "rejected" ? (
                    confirmId === item.id && confirmAction === "retry" ? (
                      <span>
                        Start a NEW attempt (previous one preserved)? Estimated cost: {estimateGate2Cost(hasVisualModification)}{" "}
                        <button className="primary" disabled={busyId === item.id} onClick={() => void runAction(item, "retry")}>
                          Confirm
                        </button>
                        <button onClick={() => setConfirmId(null)}>Cancel</button>
                      </span>
                    ) : (
                      <button
                        className="danger"
                        disabled={busyId === item.id}
                        onClick={() => {
                          setConfirmId(item.id);
                          setConfirmAction("retry");
                        }}
                      >
                        Start New Gate 2 Attempt
                      </button>
                    )
                  ) : (
                    <button onClick={() => navigate(`${basePath}/${item.gate2CurrentVideoId ?? item.gate2AutoStartVideoId}`)}>View {item.gate2VideoStatus === "finalized" ? "Finalized" : "Active"} Gate 2{(item.gate2Attempts?.length ?? 0) > 1 ? ` (${item.gate2Attempts!.length} attempts)` : ""}</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * HISTORY discoverability fix (2026-09-15): "Start New Gate 2 Attempt"
 * previously existed ONLY in the separate `GateOneApprovalsSection` card
 * below this table — a failed/rejected row up here showed no action at
 * all, so a user had no way to discover the retry option existed without
 * already knowing to scroll past the whole table to a second, differently-
 * labeled section. This renders the SAME action (`POST .../retry-gate2`,
 * reusing the already-approved Gate 1 still — never repeats Phota) inline
 * on the row itself. Never shown for `final_review_rejected` — that video
 * already has a real generated asset a human rejected at the LAST gate,
 * which gets "View Active Gate 2" instead (unchanged, see `ListView`).
 * No cost estimate shown here (unlike `GateOneApprovalsSection`'s own
 * button) — `VideoHistoryRow` carries no `hasVisualModification`
 * equivalent to compute one truthfully from, and a guessed figure would
 * risk being wrong.
 */
function RetryFailedGateTwoAction({ tenantId, personaSourceStillId, basePath }: { readonly tenantId: string; readonly personaSourceStillId: string; readonly basePath: string }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function retry(): Promise<void> {
    setBusy(true);
    setConfirming(false);
    try {
      const result = await api.post<{ videoId?: string }>(`/videos/${tenantId}/persona-source-stills/${personaSourceStillId}/retry-gate2`, null, {});
      if (result.videoId) {
        navigate(`${basePath}/${result.videoId}`);
      }
    } catch (retryError) {
      setError(retryError instanceof ApiError ? retryError.message : "Action failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <span onClick={(e) => e.stopPropagation()}>
      {error && <div className="error-banner">{error}</div>}
      {confirming ? (
        <span>
          Start a NEW Gate 2 attempt using the same approved Gate 1 image? (this attempt stays saved){" "}
          <button className="primary" disabled={busy} onClick={() => void retry()}>
            Confirm
          </button>
          <button onClick={() => setConfirming(false)}>Cancel</button>
        </span>
      ) : (
        <button className="danger" disabled={busy} onClick={() => setConfirming(true)}>
          Start New Gate 2 Attempt
        </button>
      )}
    </span>
  );
}

function FinalizedRowActions({ tenantId, videoId }: { readonly tenantId: string; readonly videoId: string }) {
  const [playing, setPlaying] = useState(false);
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setPlaying((v) => !v)}>{playing ? "Hide" : "Play"}</button>{" "}
      <a href={`/videos/${tenantId}/${videoId}/download`}>Download</a>
      {playing && (
        <div style={{ marginTop: 8 }}>
          <video className="preview-player" controls autoPlay src={`/videos/${tenantId}/${videoId}/final`} />
        </div>
      )}
    </span>
  );
}

/**
 * One Image request in the unified HISTORY list. Same columns as a Video row: TYPE = IMAGE, the source image as its identity, the request's
 * generation status, its final OUTCOME (Approved / Rejected) and the decision time. Its action is Open: the request detail shows every image
 * with its own Download (and the Bundles a request's images were saved into keep their own "Download all").
 */
function ImageHistoryRow({ item, personaName, onOpen }: { readonly item: Extract<HistoryItem, { type: "IMAGE" }>; readonly personaName: string; readonly onOpen: () => void }) {
  const request = item.request;
  return (
    <tr className="clickable" onClick={onOpen} data-testid={`history-row-${item.key}`}>
      <td>
        <span className="badge" data-testid={`history-type-${item.key}`}>
          IMAGE
        </span>
      </td>
      <td>
        <ArtifactIdentity id={request.id} thumbnailSrc={request.sourcePreviewUrl} mediaType="image" label={`Image request — ${personaName} (${request.deliveredCount} of ${request.requestedCount} images)`} />
      </td>
      <td>
        <StatusBadge status={request.status} />
      </td>
      <td>
        <span className={`badge status-${request.finalDecision ?? "unknown"}`} data-testid={`history-outcome-${item.key}`}>
          {historyOutcomeLabel(request)}
        </span>
      </td>
      <td>{new Date(request.createdAt).toLocaleString()}</td>
      <td>{new Date(item.historyTimestamp).toLocaleString()}</td>
      <td onClick={(e) => e.stopPropagation()}>
        <a href={imageRequestDetailPath(request.id)} onClick={(e) => { e.preventDefault(); onOpen(); }}>
          Open
        </a>
      </td>
    </tr>
  );
}

/**
 * The ONE unified HISTORY table: Video and Image rows together, in the order given (already sorted by `historyTimestamp`).
 * Video rows are rendered exactly as before; Image rows are `ImageHistoryRow`.
 */
function HistoryTable({ items, tenantId, personaNames, basePath }: { readonly items: readonly HistoryItem[]; readonly tenantId: string; readonly personaNames: Record<string, string>; readonly basePath: string }) {
  const navigate = useNavigate();
  return (
    <table>
      <thead>
        <tr>
          <th>Type</th>
          <th>Item</th>
          <th>Status</th>
          <th>Outcome</th>
          <th>Created</th>
          <th>Last Update</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          if (item.type === "IMAGE") {
            return <ImageHistoryRow key={item.key} item={item} personaName={personaNames[item.request.personaId] ?? item.request.personaId} onOpen={() => navigate(imageRequestDetailPath(item.request.id))} />;
          }
          const video = item.video;
          return (
          <tr key={item.key} className="clickable" onClick={() => navigate(`${basePath}/${video.id}`)}>
            <td>
              <span className="badge" data-testid={`history-type-${item.key}`}>
                VIDEO
              </span>
            </td>
            <td>
              {/* History root-fix (2026-09-16, real production incident: opening History rendered a <video preload="metadata"> per finalized row pointed at the full /final endpoint — a stream-abort storm that contributed to two separate process crashes). A finalized row's thumbnail is now the SMALL, one-time-generated `/thumbnail` object (see `FinalizeVideo`/`handleThumbnail`'s own doc comments) — never the full video. A historical finalized video with no thumbnail (`hasThumbnail: false`) falls back to ArtifactIdentity's own placeholder, never `/final`. A non-finalized row uses the Gate 1 still ONLY when a real, content-addressed checksum is available (`personaSourceStillPreviewChecksumSha256`) — never a bare, non-cacheable `/preview` request; otherwise it also falls back to the placeholder. `/final` is now reachable ONLY via the explicit Play action below, never merely by rendering this list. */}
              <ArtifactIdentity
                id={video.id}
                thumbnailSrc={
                  video.status === "finalized"
                    ? video.hasThumbnail
                      ? `/videos/${tenantId}/${video.id}/thumbnail`
                      : null
                    : video.personaSourceStillId !== null && video.personaSourceStillPreviewChecksumSha256 !== null
                      ? `/videos/${tenantId}/persona-source-stills/${video.personaSourceStillId}/preview?checksum=${video.personaSourceStillPreviewChecksumSha256}`
                      : null
                }
                mediaType="image"
                label={video.humanReadableName}
              />
            </td>
            <td>
              <StatusBadge status={video.status} />
            </td>
            <td>{video.outcomeLabel}</td>
            <td>{new Date(video.createdAt).toLocaleString()}</td>
            <td>{new Date(video.updatedAt).toLocaleString()}</td>
            <td>
              {video.status === "finalized" ? (
                <FinalizedRowActions tenantId={tenantId} videoId={video.id} />
              ) : (video.status === "failed" || video.status === "rejected") && video.personaSourceStillId !== null ? (
                <RetryFailedGateTwoAction tenantId={tenantId} personaSourceStillId={video.personaSourceStillId} basePath={basePath} />
              ) : null}
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ListView({ tenantId, basePath }: { readonly tenantId: string; readonly basePath: string }) {
  const [videos, setVideos] = useState<VideoHistoryRow[]>([]);
  const [images, setImages] = useState<ImageBatchListItem[]>([]);
  const [personaNames, setPersonaNames] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [imagesUnavailable, setImagesUnavailable] = useState(false);
  const [filter, setFilter] = useState<HistoryFilter>("ALL");

  // Unified HISTORY: Videos and Image requests (the ones with a final Approved / Rejected decision) load INDEPENDENTLY into ONE list -
  // a failure of one type never hides the other.
  const loadVideos = useCallback(async () => {
    try {
      const result = await api.get<{ videos: VideoHistoryRow[] }>(`/videos/${tenantId}?limit=200`);
      setVideos(result.videos);
      // A successful refresh clears a stale error from an earlier failed poll — no page refresh needed.
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof ApiError ? loadError.message : "Failed to load history.");
    }
  }, [tenantId]);

  const loadImages = useCallback(async () => {
    try {
      const result = await api.get<{ batches: ImageBatchListItem[] }>(`/images/${encodeURIComponent(tenantId)}/batches?review=decided`);
      setImages(result.batches);
      setImagesUnavailable(false);
    } catch {
      setImagesUnavailable(true);
    }
  }, [tenantId]);

  const load = useCallback(async () => {
    await Promise.all([loadVideos(), loadImages()]);
  }, [loadVideos, loadImages]);

  useEffect(() => {
    api
      .get<{ personas: ImagePersonaOption[] }>(`/images/${encodeURIComponent(tenantId)}/personas`)
      .then((result) => setPersonaNames(Object.fromEntries(result.personas.map((p) => [p.personaId, p.displayName]))))
      .catch(() => undefined);
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  useReviewEventStream(tenantId, load);

  useEffect(() => {
    const interval = setInterval(() => void load(), HISTORY_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  if (error) {
    // Same as before for the Video side (the banner replaces the Video list, its filters and Gate 1 Approvals); the only difference is that
    // the Image requests that DID load are not hidden by a Video failure.
    const imageRows = mergeHistoryItems([], images);
    return (
      <div>
        <div className="error-banner">{error}</div>
        {imageRows.length > 0 && (
          <div className="card">
            <HistoryTable items={imageRows} tenantId={tenantId} personaNames={personaNames} basePath={basePath} />
          </div>
        )}
      </div>
    );
  }

  const items = mergeHistoryItems(videos.filter(isHistoryEligible), images);
  const filtered = items.filter((item) => matchesHistoryFilter(item, filter, matchesFilter));

  return (
    <div>
      <div className="card">
        {imagesUnavailable && images.length === 0 && (
          <p className="muted" data-testid="history-images-unavailable">
            Image requests are unavailable right now.
          </p>
        )}
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {FILTERS.map((f) => (
            <button key={f} className={filter === f ? "primary" : ""} onClick={() => setFilter(f)}>
              {f.replace("_", " ")}
            </button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="empty-state">No history items match this filter.</div>
        ) : (
          <HistoryTable items={filtered} tenantId={tenantId} personaNames={personaNames} basePath={basePath} />
        )}
      </div>
      <GateOneApprovalsSection tenantId={tenantId} basePath={basePath} />
    </div>
  );
}

/**
 * A video the user already approved at Gate 2 is blocked when its transcription failed and the automatic bounded retries are spent (for
 * example after a Google Speech quota outage): nothing after it (translation, the paid upscale, subtitle burn, final review) starts until
 * it succeeds. This card explains that and offers the ONE recovery: ask the automation to retry the transcription with a fresh bounded
 * budget. It only appears when the server says a retry is available (`transcriptionManualRetryAvailable`), and needs OPERATOR+.
 */
function TranscriptionRetryCard({ tenantId, videoId, failureReason, onRetried }: { readonly tenantId: string; readonly videoId: string; readonly failureReason: string | null; readonly onRetried: () => void }) {
  const { user, csrfToken, memberships } = useAuth();
  const role = effectiveImageRole({ isPlatformOwner: user?.isPlatformOwner === true, memberships: memberships ?? [], tenantId });
  const canRetry = roleAtLeast(role, OPERATOR_ACTION_ROLE);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function retry(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/videos/${tenantId}/${videoId}/retry-transcription`, csrfToken, {});
      setConfirming(false);
      onRetried();
    } catch (retryError) {
      setError(retryError instanceof ApiError ? retryError.message : "Retry failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="error-banner" style={{ marginTop: 16 }} data-testid="transcription-retry-card">
      <strong>Waiting for a manual retry: transcription failed</strong>
      <p>
        The speech transcription of this video failed and the automatic retries ran out, so the rest of the pipeline (translation, upscale, subtitles, final review) has not started.
        {failureReason ? ` Last error: ${failureReason}` : ""}
      </p>
      {error && <div className="error-banner">{error}</div>}
      {!canRetry ? (
        <p className="muted">An operator can retry it.</p>
      ) : confirming ? (
        <div data-testid="transcription-retry-confirm">
          <p>
            Retry the transcription now? It makes one more Google Speech request (a small charge). If it succeeds, this video continues by itself to translation, the paid upscale and final review.
          </p>
          <button className="primary" disabled={busy} onClick={() => void retry()} data-testid="transcription-retry-confirm-yes">
            Yes, retry transcription
          </button>{" "}
          <button disabled={busy} onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </div>
      ) : (
        <button className="primary" onClick={() => setConfirming(true)} data-testid="transcription-retry-button">
          Retry transcription
        </button>
      )}
    </div>
  );
}

function DetailView({ tenantId, basePath }: { readonly tenantId: string; readonly basePath: string }) {
  const { user } = useAuth();
  const { videoId } = useParams();
  const [progress, setProgress] = useState<(VideoProgress & { jobs?: JobDetail[]; transcriptionManualRetryAvailable?: boolean }) | null>(null);
  const [retryNotice, setRetryNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showOwnerDetails, setShowOwnerDetails] = useState(false);

  const loadProgress = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      if (videoId === undefined) return;
      try {
        const result = await api.get<VideoProgress & { jobs?: JobDetail[]; transcriptionManualRetryAvailable?: boolean }>(`/videos/${tenantId}/${videoId}/progress`);
        if (!isCancelled()) setProgress(result);
      } catch (loadError) {
        if (!isCancelled()) setError(loadError instanceof ApiError ? loadError.message : "Failed to load video detail.");
      }
    },
    [tenantId, videoId],
  );

  useEffect(() => {
    let cancelled = false;
    void loadProgress(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [loadProgress]);

  if (error) return <div className="error-banner">{error}</div>;
  if (progress === null) return <div className="empty-state">Loading…</div>;

  return (
    <div>
      <a href={basePath}>&larr; Back to History</a>
      <div className="card" style={{ marginTop: 12 }}>
        <h2>
          Video <code>{progress.videoId.slice(0, 8)}</code> <CopyIdButton id={progress.videoId} /> <StatusBadge status={progress.videoStatus} />
        </h2>
        <div className="kv">
          {/* Two-fixes-follow-up (2026-09): the Preview/Overall progress PERCENTAGE rows were removed — WaveSpeed's real API returns no progress/percentage field at all (see derive-video-generation-progress.ts's own doc comment), so those numbers were fabricated per-milestone constants, never a real measurement. "Stage" below is the real, durable-state-derived milestone label — never a percentage that doesn't exist. */}
          <dt>Stage</dt>
          <dd>{progress.progressStage}</dd>
          <dt>Last update</dt>
          <dd>{new Date(progress.updatedAt).toLocaleString()}</dd>
          <dt>Tracked cost</dt>
          <dd>
            {progress.costSummary.totalActualCostUsd !== null
              ? `$${progress.costSummary.totalActualCostUsd.toFixed(2)}`
              : `~$${progress.costSummary.totalEstimatedCostUsd.toFixed(2)} (estimated, ${progress.costSummary.completeness})`}
          </dd>
        </div>

        {retryNotice && (
          <p className="muted" style={{ marginTop: 16 }} data-testid="transcription-retry-notice">
            {retryNotice}
          </p>
        )}
        {progress.transcriptionManualRetryAvailable === true && (
          <TranscriptionRetryCard
            tenantId={tenantId}
            videoId={progress.videoId}
            failureReason={progress.jobs?.find((job) => job.jobType === "transcription")?.failureReason ?? null}
            onRetried={() => {
              setRetryNotice("Retry requested. The automation retries the transcription within about 15 seconds; this video continues by itself if it succeeds.");
              void loadProgress();
            }}
          />
        )}

        {progress.failureDetail && (
          <div className="error-banner" style={{ marginTop: 16 }}>
            <strong>FAILED</strong>
            <div className="kv" style={{ marginTop: 8 }}>
              <dt>Stage</dt>
              <dd>{progress.failureDetail.failureStage}</dd>
              <dt>Outcome</dt>
              <dd>{progress.failureDetail.verificationOutcome ?? "—"}</dd>
              <dt>Reason</dt>
              <dd>{progress.failureDetail.failureReason}</dd>
              {progress.failureDetail.needsManualReconciliation && (
                <>
                  <dt>Manual reconciliation</dt>
                  <dd>Required</dd>
                </>
              )}
            </div>
          </div>
        )}

        {progress.videoStatus === "finalized" && (
          <div style={{ marginTop: 16 }}>
            {/* Section 4 fix: PLAY now streams the REAL final canonical object (videos/final/:videoId/), never the Gate 2 staging/review preview. */}
            <video className="preview-player" controls src={`/videos/${tenantId}/${progress.videoId}/final`} />
            <div style={{ marginTop: 8 }}>
              <a href={`/videos/${tenantId}/${progress.videoId}/download`}>Download</a>
            </div>
            {/* FIX-010 correction (2026-09): these warnings were already
                durably persisted (finalization/upscale/subtitle-burn) but
                previously never reached this view — the video remains
                playable/downloadable exactly as before; this is
                additional truthful information, never another gate. */}
            {progress.warnings.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {progress.warnings.map((warning) => (
                  <p key={warning} className="warning-banner" style={{ color: "var(--warning, #b45309)" }}>
                    ⚠ {warning}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {user?.isPlatformOwner && progress.jobs && (
          <div style={{ marginTop: 20 }}>
            <button onClick={() => setShowOwnerDetails((v) => !v)}>{showOwnerDetails ? "Hide" : "Show"} Owner Details</button>
            {showOwnerDetails && (
              <table style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Type</th>
                    <th>Engine</th>
                    <th>Model</th>
                    <th>Status</th>
                    <th>Prediction ID</th>
                    <th>Est. cost</th>
                    <th>Actual cost</th>
                    <th>Failure reason</th>
                    <th>Last provider error</th>
                    <th>Visual mod outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {progress.jobs.map((job) => (
                    <tr key={job.id}>
                      <td>{job.id}</td>
                      <td>{job.jobType}</td>
                      <td>{job.engine}</td>
                      <td>{job.modelId ?? "—"}</td>
                      <td>{job.status}</td>
                      <td>{job.providerPredictionId ?? "—"}</td>
                      <td>{job.estimatedCostUsd !== null ? `$${job.estimatedCostUsd.toFixed(2)}` : "—"}</td>
                      <td>{job.actualCostUsd !== null ? `$${job.actualCostUsd.toFixed(2)}` : "—"}</td>
                      <td>{job.failureReason ?? "—"}</td>
                      <td>{job.lastProviderError ?? "—"}</td>
                      <td>{job.visualModificationOutcome ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * `basePath` (FIX-045, Admin: View external user/workspace History) —
 * where every internal navigation (row click, back link, retry/Gate-2
 * redirects) points. Defaults to the original literal so the GENERATE
 * tab's own usage (`GeneratePage.tsx`) is byte-identical to before this
 * prop existed. `AdminWorkspaceHistoryPage` passes its own
 * `/admin/workspaces/:tenantId/history` instead, which is what lets this
 * exact same component (never a duplicate) work correctly when the OWNER
 * opens an external workspace's History from Admin.
 */
export function HistoryTab({ tenantId, basePath = "/generate/history" }: { readonly tenantId: string; readonly basePath?: string }) {
  return (
    <Routes>
      <Route path="/" element={<ListView tenantId={tenantId} basePath={basePath} />} />
      <Route path=":videoId" element={<DetailView tenantId={tenantId} basePath={basePath} />} />
    </Routes>
  );
}
